<script setup lang="ts">
import usePoolStore from '~~/app/store/pool'
import { formatDistance } from '~~/app/utils/format'
import CategoriesIcons from './CategoriesIcons.vue'
import type { MapPool } from '~~/interfaces/map'

const { t } = useI18n()

const getLocationType = (item: MapPool): string =>
    item?.pool?.locationTypeSlug || 'unknown'

const distanceUnit = (distance: number): string => {
    if (distance < 1000) {
        return t('meters')
    }

    return 'km'
}

type AcceptedPoolProperties =
    | 'name'
    | 'number'
    | 'streetName'
    | 'postalCode'
    | 'city'

const getItemPoolProperty = (
    item: MapPool,
    property: AcceptedPoolProperties
): string => item.pool[property] || ''

const getItemDistance = (item: MapPool): number => item.distance || 0

const onClickOnAPool = (item: MapPool) => {
    if (window.location !== window?.parent?.location) {
        ;(window.top as Window).postMessage(
            {
                action: 'openUrl',
                url: `${item.pool.slug}.html`,
                target: '_blank'
            },
            document.referrer
        )
    } else {
        return navigateTo(getChargemapUrl(`/${item.pool.slug}.html`), {
            external: true
        })
    }
}

onMounted(() => usePoolStore().fetchNearStations())
</script>

<template>
    <div>
        <Loader v-if="usePoolStore().mappyLoader" class="min-h-140" />
        <div v-else class="text-sm">
            <div
                v-for="(item, index) of usePoolStore().nearStations"
                :key="getItemPoolProperty(item, 'name')"
            >
                <div
                    v-if="getItemDistance(item) !== 0"
                    class="flex flex-col w-full min-h-70 relative my-3"
                >
                    <div
                        class="flex justify-between w-full mb-3 cursor-pointer"
                        @click="onClickOnAPool(item)"
                    >
                        <div
                            class="flex items-center justify-center p-2.5 min-w-70 min-h-70 text-3xl rounded-lg border-1 overflow-hidden w-auto h-full border-gray-l-200 mr-3"
                        >
                            <CategoriesIcons
                                v-if="getLocationType(item)"
                                class="size-8"
                                :category="getLocationType(item)"
                            />
                        </div>
                        <div class="flex justify-between w-full items-center">
                            <div class="text-black text-xs">
                                <h3
                                    class="text-gray-l-800 text-0.5sm text-left"
                                >
                                    {{ getItemPoolProperty(item, 'name') }}
                                </h3>
                                <p>
                                    {{ getItemPoolProperty(item, 'number') }}
                                    {{
                                        getItemPoolProperty(item, 'streetName')
                                    }}
                                </p>
                                <p>
                                    {{
                                        getItemPoolProperty(item, 'postalCode')
                                    }}
                                    {{ getItemPoolProperty(item, 'city') }}
                                </p>
                            </div>
                            <div class="text-center w-max ml-2">
                                <p class="text-3xl text-black">
                                    {{ formatDistance(getItemDistance(item)) }}
                                </p>
                                <p class="text-sm text-[#A1A1AA]">
                                    {{ distanceUnit(getItemDistance(item)) }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <span
                        v-if="
                            index !==
                            (usePoolStore().nearStations?.length ?? 0) - 1
                        "
                        class="w-full border-b-1 border-gray-l-200"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
